import React, { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";

type AnchorRect = { top: number; left: number; width: number; height: number };

type Props = {
  openKey: string | null;
  anchor: AnchorRect | null;
  loading: boolean;
  search: string;
  onSearchChange: (v: string) => void;
  options: string[];
  onSelect: (value: string) => void;
  onClear: () => void;
  onClose: () => void;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export function FilterPopover({
  openKey,
  anchor,
  loading,
  search,
  onSearchChange,
  options,
  onSelect,
  onClear,
  onClose,
}: Props): JSX.Element | null {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const filtered = useMemo(() => {
    const s = (search || "").toLowerCase();
    if (!s) return options;
    return options.filter((o) => o.toLowerCase().includes(s));
  }, [options, search]);

  useEffect(() => {
    if (openKey && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [openKey]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!openKey || !anchor) return null;

  const viewportW = typeof window !== "undefined" ? window.innerWidth : 1200;
  const viewportH = typeof window !== "undefined" ? window.innerHeight : 800;
  const desiredTop = anchor.top + anchor.height + 8;
  const left = clamp(anchor.left - 80, 8, viewportW - 380);
  const top = clamp(desiredTop, 8, viewportH - 360);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Filter ${openKey}`}
      className="fixed inset-0 z-40"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" />
      <div
        style={{
          top,
          left,
        }}
        className="absolute grid w-[360px] max-w-[calc(100%-24px)] gap-2 rounded-xl bg-surface p-3 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          type="text"
          className="gw-input"
          placeholder="Sök värde…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />

        {loading && <div className="text-sm text-muted">Laddar värden…</div>}

        <div className="max-h-[220px] overflow-auto rounded-lg border border-border scrollbar scrollbar-thumb-primary scrollbar-track-surface">
          {filtered.length === 0 && (
            <div className="p-2 text-sm text-muted">Inga träffar</div>
          )}
          {filtered.map((v) => (
            <button
              key={v || "(empty)"}
              className="gw-btn gw-btn-ghost w-full justify-start rounded-none"
              onClick={() => onSelect(v)}
            >
              {v || "(tomt värde)"}
            </button>
          ))}
        </div>

        <div className="flex justify-end gap-2">
          <button className="gw-btn gw-btn-secondary" onClick={onClear}>Rensa</button>
          <button className="gw-btn gw-btn-secondary" onClick={onClose}>Stäng</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export type { AnchorRect };
